"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Heart, ShieldCheck, MapPin } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function CareWithAttentionAtHome() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="zorg-met-aandacht-bij-u-thuis" className="relative overflow-hidden bg-gradient-to-b from-background to-secondary/60 pt-24 pb-16 md:pt-32 md:pb-24 lg:pt-40 lg:pb-32">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-accent/30 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-7 flex flex-col justify-center transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 w-fit">
              <Heart className="w-4 h-4" />
              <span>CuraNova Zorg</span>
            </div>
            
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.15] tracking-tight mb-6">
              Zorg met aandacht, <br className="hidden sm:block" />
              <span className="text-foreground">bij u thuis</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-foreground/80 leading-relaxed max-w-2xl mb-10">
              Wij bieden persoonsgerichte extramurale zorg en ondersteuning, volledig afgestemd op uw persoonlijke situatie, wensen en behoeften. Met vaste, gediplomeerde zorgprofessionals brengen wij kwaliteit en vertrouwen bij u thuis.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button size="lg" className="text-base h-14 px-8 rounded-xl shadow-md" asChild>
                <Link to="/ContactPage">
                  Vraag een kennismakingsgesprek aan
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="text-base h-14 px-8 rounded-xl bg-background/50 backdrop-blur-sm border-primary/20 hover:bg-primary/5" asChild>
                <Link to="/ContactPage">
                  Bespreek uw zorgvraag
                </Link>
              </Button>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-8 border-t border-border/60">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent flex items-center justify-center text-primary">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium text-foreground/90">Gediplomeerde professionals</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent flex items-center justify-center text-primary">
                  <MapPin className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium text-foreground/90">Regio Den Haag & Rotterdam</span>
              </div>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`lg:col-span-5 relative transition-all duration-1000 delay-200 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="relative w-full max-w-md mx-auto lg:max-w-none">
              {}
              <div className="absolute inset-0 bg-accent/40 rounded-[3rem_1.5rem_4rem_2rem] transform rotate-3 scale-105 transition-transform duration-700 hover:rotate-6"></div>
              
              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576260/img/caregiver_client_home_interaction.png" alt="Zorgverlener van CuraNova Zorg in gesprek met een cliënt in de thuissituatie" className="relative z-10 w-full h-auto object-cover rounded-[2.5rem_1.5rem_3.5rem_2rem] shadow-2xl shadow-primary/10 border-4 border-background/50" loading="eager" />
              
              {}
              <div className="absolute -bottom-6 -left-6 z-20 bg-background rounded-2xl p-4 shadow-xl shadow-primary/5 border border-border/50 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-500">
                <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-primary">
                  <Heart className="w-6 h-6 fill-primary/20" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">Persoonsgericht</p>
                  <p className="text-xs text-muted-foreground">Zorg op maat</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}